import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { COLORS, DESIGN_TOKENS } from '@src/constants/design-tokens';

const DARK_BACKGROUND_GRADIENT = `linear-gradient(
  180deg,
  #141414 0%,
  #0f0f0f 35%,
  #0a0a0a 70%,
  ${COLORS.darkMode.background.default} 100%
)`;

/**
 * MUIテーマの基本設定
 * 元のスタイリングを維持するため、最小限の設定のみ
 */
const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: COLORS.primary.main,
      light: COLORS.primary.light,
      dark: COLORS.primary.dark,
      contrastText: COLORS.primary.contrastText,
    },
    secondary: {
      main: COLORS.secondary.main,
      light: COLORS.secondary.light,
      dark: COLORS.secondary.dark,
      contrastText: COLORS.secondary.contrastText,
    },
    background: {
      default: COLORS.background.default,
      paper: COLORS.background.paper,
    },
    text: {
      primary: COLORS.text.primary,
      secondary: COLORS.text.secondary,
      disabled: COLORS.text.disabled,
    },
    error: {
      main: COLORS.semantic.error.main,
      light: COLORS.semantic.error.light,
      dark: COLORS.semantic.error.dark,
    },
    warning: {
      main: COLORS.semantic.warning.main,
      light: COLORS.semantic.warning.light,
      dark: COLORS.semantic.warning.dark,
    },
    info: {
      main: COLORS.semantic.info.main,
      light: COLORS.semantic.info.light,
      dark: COLORS.semantic.info.dark,
    },
    success: {
      main: COLORS.semantic.success.main,
      light: COLORS.semantic.success.light,
      dark: COLORS.semantic.success.dark,
    },
  },
  typography: {
    fontFamily: DESIGN_TOKENS.typography.fontFamily.primary,
    h1: DESIGN_TOKENS.typography.headings.h1,
    h2: DESIGN_TOKENS.typography.headings.h2,
    h3: DESIGN_TOKENS.typography.headings.h3,
    h4: DESIGN_TOKENS.typography.headings.h4,
    h5: DESIGN_TOKENS.typography.headings.h5,
    h6: DESIGN_TOKENS.typography.headings.h6,
    body1: {
      fontSize: DESIGN_TOKENS.typography.fontSize.base,
      lineHeight: DESIGN_TOKENS.typography.lineHeight.normal,
      fontWeight: DESIGN_TOKENS.typography.fontWeight.normal, // 500に
    },
    body2: {
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      lineHeight: DESIGN_TOKENS.typography.lineHeight.normal,
      fontWeight: DESIGN_TOKENS.typography.fontWeight.normal, // 500に
    },
    button: {
      fontSize: DESIGN_TOKENS.typography.fontSize.sm,
      fontWeight: DESIGN_TOKENS.typography.fontWeight.medium, // 600に
      textTransform: 'none',
    },
    caption: {
      fontSize: DESIGN_TOKENS.typography.fontSize.xs,
      lineHeight: DESIGN_TOKENS.typography.lineHeight.tight,
    },
  },

  // レスポンシブブレークポイント
  breakpoints: {
    values: {
      xs: parseInt(DESIGN_TOKENS.breakpoints.xs, 10),
      sm: parseInt(DESIGN_TOKENS.breakpoints.sm, 10),
      md: parseInt(DESIGN_TOKENS.breakpoints.md, 10),
      lg: parseInt(DESIGN_TOKENS.breakpoints.lg, 10),
      xl: parseInt(DESIGN_TOKENS.breakpoints.xl, 10),
    },
  },

  spacing: 8, // 8pxをベースとしたスペーシング

  shape: {
    borderRadius: parseInt(DESIGN_TOKENS.borderRadius.md, 10),
  },
  components: {
    // レスポンシブ対応とモダンなスタイルを追加
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: COLORS.background.default,
          color: COLORS.text.primary,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: COLORS.scrollbar.track,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.scrollbar.thumb,
            borderRadius: DESIGN_TOKENS.borderRadius.sm,
          },
        },
        '#root': {
          backgroundColor: COLORS.background.default,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          textTransform: 'none',
          fontWeight: DESIGN_TOKENS.typography.fontWeight.medium,
          padding: `${DESIGN_TOKENS.spacing.sm} ${DESIGN_TOKENS.spacing.md}`,
          transition: DESIGN_TOKENS.animation.transitions.all,

          // モバイル対応のタッチターゲット
          minHeight: '44px',
          '@media (max-width: 768px)': {
            fontSize: DESIGN_TOKENS.typography.fontSize.md,
            minHeight: '52px',
            padding: '14px 18px',
          },
          '@media (max-width: 480px)': {
            fontSize: '1.05rem',
            minHeight: '56px',
            padding: '16px 22px',
          },

          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: DESIGN_TOKENS.shadows.md,
          },

          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: DESIGN_TOKENS.shadows.sm,
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transform: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: DESIGN_TOKENS.borderRadius.lg,
          boxShadow: DESIGN_TOKENS.shadows.sm,
          transition: DESIGN_TOKENS.animation.transitions.all,
          border: `1px solid ${COLORS.border.light}`,

          '&:hover': {
            boxShadow: DESIGN_TOKENS.shadows.md,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            transition: DESIGN_TOKENS.animation.transitions.all,

            // モバイル対応
            minHeight: '44px',
            '@media (max-width: 768px)': {
              minHeight: '50px',
              '& .MuiInputBase-input': {
                fontSize: DESIGN_TOKENS.typography.fontSize.md,
                padding: '14px 12px',
              },
            },
            '@media (max-width: 480px)': {
              minHeight: '54px',
              '& .MuiInputBase-input': {
                fontSize: '1rem',
                padding: '16px 14px',
              },
            },

            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.primary.light,
              },
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
                borderColor: COLORS.primary.main,
              },
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 768px)': {
            paddingLeft: DESIGN_TOKENS.spacing.md,
            paddingRight: DESIGN_TOKENS.spacing.md,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: DESIGN_TOKENS.shadows.sm,
          // デフォルトは透明。各画面で必要に応じて上書き（Navigationで白/フォーカスで黒）
          backgroundColor: 'transparent',
          color: 'inherit',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,

          '& .MuiButton-text': {
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'inherit',
              transform: 'none',
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: 'transparent',
              transform: 'none',
            },
            '&:focus': {
              outline: 'none',
              backgroundColor: 'transparent',
            },
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: COLORS.text.secondary,
            transition: 'transform 0.2s ease-in-out',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            color: COLORS.primary.main,
          },
          '&:hover .MuiAccordionSummary-expandIconWrapper': {
            color: COLORS.text.primary,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s ease-in-out',
          '& .MuiAccordionDetails-root': {
            transition: 'all 0.3s ease-in-out',
          },
        },
      },
    },
  },
};

/**
 * ダークモード用のMUIテーマオプション
 */
const darkModeOverrides: ThemeOptions = {
  palette: {
    mode: 'dark',
    background: {
      default: COLORS.darkMode.background.default,
      paper: COLORS.darkMode.background.paper,
    },
    text: {
      primary: COLORS.darkMode.text.primary,
      secondary: COLORS.darkMode.text.secondary,
      disabled: COLORS.darkMode.text.disabled,
    },
    primary: {
      main: COLORS.focusMode.text.accent, // フォーカスモードと同じアクセントカラーを使用
      light: COLORS.focusMode.text.accent,
      dark: COLORS.focusMode.text.accent,
      contrastText: COLORS.darkMode.background.default,
    },
    divider: COLORS.darkMode.divider,
    action: COLORS.darkMode.action,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.focusMode.border.primary}`,
        },
        contained: {
          backgroundColor: COLORS.focusMode.button.primary.background,
          color: COLORS.focusMode.button.primary.text,
          borderColor: COLORS.focusMode.button.primary.border,
          '&:hover': {
            backgroundColor: COLORS.focusMode.button.primary.hover,
          },
        },
        outlined: {
          backgroundColor: COLORS.focusMode.button.secondary.background,
          color: COLORS.focusMode.button.secondary.text,
          borderColor: COLORS.focusMode.button.secondary.border,
          '&:hover': {
            backgroundColor: COLORS.focusMode.button.secondary.hover,
          },
        },
        text: {
          backgroundColor: COLORS.focusMode.button.ghost.background,
          color: COLORS.focusMode.button.ghost.text,
          borderColor: COLORS.focusMode.button.ghost.border,
          '&:hover': {
            backgroundColor: COLORS.focusMode.button.ghost.hover,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.focusMode.card.background,
          boxShadow: COLORS.focusMode.card.shadow,
          border: `1px solid ${COLORS.focusMode.card.border}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: COLORS.focusMode.input.background,
            color: COLORS.focusMode.input.text,

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: COLORS.focusMode.input.border,
            },

            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.focusMode.border.secondary,
              },
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: COLORS.focusMode.input.borderFocused,
              },
            },

            '& .MuiInputBase-input::placeholder': {
              color: COLORS.focusMode.input.placeholder,
              opacity: 1,
            },
          },

          '& .MuiInputLabel-root': {
            color: COLORS.focusMode.text.secondary,
            '&.Mui-focused': {
              color: COLORS.focusMode.text.accent,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: COLORS.focusMode.menu.background,
          borderRight: `1px solid ${COLORS.focusMode.menu.border}`,
          boxShadow: COLORS.focusMode.menu.shadow,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.focusMode.menu.background,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: COLORS.focusMode.menu.hover,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: COLORS.focusMode.text.primary,
        },
        secondary: {
          color: COLORS.focusMode.text.secondary,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: COLORS.focusMode.border.primary,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.focusMode.accordion.summary.background,
          borderBottom: `1px solid ${COLORS.focusMode.accordion.summary.border}`,
          borderTopLeftRadius: parseInt(DESIGN_TOKENS.borderRadius.lg, 10),
          borderTopRightRadius: parseInt(DESIGN_TOKENS.borderRadius.lg, 10),
          minHeight: 52,
          paddingLeft: 12,
          paddingRight: 12,
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            color: COLORS.darkMode.text.primary,
            fontWeight: 700,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: COLORS.darkMode.text.secondary,
            transition: 'transform 0.2s ease-in-out',
          },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            color: COLORS.focusMode.text.accent,
          },
          '&:hover': {
            backgroundColor: COLORS.focusMode.accordion.summary.backgroundHover,
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: COLORS.darkMode.text.primary,
            },
          },
          '&.Mui-expanded': {
            minHeight: 52,
            backgroundColor: COLORS.focusMode.accordion.summary.backgroundExpanded,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.focusMode.accordion.root.background,
          border: `1px solid ${COLORS.focusMode.accordion.root.border}`,
          borderRadius: 14,
          marginBottom: 10,
          boxShadow: 'none',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordionDetails-root': {
            backgroundColor: COLORS.focusMode.accordion.details.background,
            borderTop: `1px solid ${COLORS.focusMode.accordion.details.border}`,
            padding: '12px 16px',
            color: COLORS.darkMode.text.secondary,
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: COLORS.darkMode.background.default,
          backgroundImage: DARK_BACKGROUND_GRADIENT,
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          color: COLORS.darkMode.text.primary,
          scrollbarColor: `${COLORS.darkScrollbar.thumb} ${COLORS.darkScrollbar.track}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            backgroundColor: COLORS.darkScrollbar.track,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: COLORS.darkScrollbar.thumb,
            minHeight: 24,
            border: `3px solid ${COLORS.darkScrollbar.track}`,
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: COLORS.darkScrollbar.thumbHover,
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: COLORS.darkScrollbar.thumbHover,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: COLORS.darkScrollbar.thumbHover,
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: COLORS.darkScrollbar.track,
          },
        },
        '#root': {
          backgroundColor: COLORS.darkMode.background.default,
          backgroundImage: DARK_BACKGROUND_GRADIENT,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.darkMode.background.paper,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
};

/**
 * アプリケーション用のMUIテーマ
 */
export const theme = createTheme(baseThemeOptions);

/**
 * フォーカスモード用のMUIテーマ
 * 色味や挙動はダークモードと共通化し、背景やUI配置の差分はコンポーネント側で扱う
 */
export const focusModeTheme = createTheme(baseThemeOptions, darkModeOverrides);

/**
 * ダークモード用のMUIテーマ
 */
export const darkTheme = createTheme(baseThemeOptions, darkModeOverrides);

// スタイルコンポーネントのエクスポート
export { StyledSection } from './components/StyledComponents';
